import { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useAuthStore } from 'src/app/auth/store/auth-store'
import { Toast, toastTypes } from 'src/app/core/services/toast'
import { useListUrlState } from 'src/app/home/hooks/use-list-url-state'
import { useMemberInsightCategoriesQuery } from 'src/app/home/repositories/use-member-insight-categories-query'
import { useMemberInsightDownloadQuery } from 'src/app/home/repositories/use-member-insight-download-query'
import { useMemberInsightProducersQuery } from 'src/app/home/repositories/use-member-insight-producers-query'
import { useMemberInsightsQuery } from 'src/app/home/repositories/use-member-insights-query'

import { applyProducerFilter } from '../domain/filter-insights'
import {
  IInsightEntity,
  INSIGHT_CATEGORY_ALL,
  InsightCategoryFilter,
  IUseMemberInsightsReturn,
} from '../types'

const PAGE_SIZE = 6

export const useMemberInsights = (): IUseMemberInsightsReturn => {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())

  const {
    page: currentPage,
    searchQuery: searchTerm,
    debouncedSearchQuery: debouncedSearchTerm,
    filters,
    setPage: setCurrentPage,
    setSearchQuery: setSearchTerm,
    setFilter,
  } = useListUrlState({
    searchEnabled: true,
    filters: [
      { key: 'category', default: INSIGHT_CATEGORY_ALL },
      { key: 'producer', default: '' },
    ],
  })

  const categoryFilter = filters.category as InsightCategoryFilter
  const producerFilter = filters.producer
  const setCategoryFilter = useCallback(
    (value: InsightCategoryFilter): void => setFilter('category', value),
    [setFilter],
  )
  const setProducerFilter = useCallback(
    (value: string): void => setFilter('producer', value),
    [setFilter],
  )

  const [selectedInsight, setSelectedInsight] = useState<IInsightEntity | null>(
    null,
  )
  const [selectedInsightBlobUrl, setSelectedInsightBlobUrl] = useState<
    string | null
  >(null)

  const downloadQuery = useMemberInsightDownloadQuery({
    downloadUrl: selectedInsight?.downloadUrl ?? '',
    enabled: !!selectedInsight,
  })

  useEffect((): (() => void) | void => {
    if (!downloadQuery.data || !selectedInsight?.id) {
      return
    }
    const url = URL.createObjectURL(downloadQuery.data)
    setSelectedInsightBlobUrl(url)
    return (): void => {
      URL.revokeObjectURL(url)
    }
  }, [downloadQuery.data, selectedInsight?.id])

  useEffect((): void => {
    if (!downloadQuery.isError) {
      return
    }
    Toast.notify({
      type: toastTypes.ERROR,
      title: 'Failed to download document',
      message: 'This document could not be downloaded. Please try again.',
      options: { toastId: 'member-insight-download-error' },
    })
  }, [downloadQuery.isError])

  const openInsightPreview = useCallback((insight: IInsightEntity): void => {
    setSelectedInsightBlobUrl(null)
    setSelectedInsight(insight)
  }, [])

  const closeInsightPreview = useCallback((): void => {
    setSelectedInsight(null)
    setSelectedInsightBlobUrl(null)
  }, [])

  const listQuery = useMemberInsightsQuery({
    page: currentPage,
    pageSize: PAGE_SIZE,
    search: debouncedSearchTerm,
    category: categoryFilter === INSIGHT_CATEGORY_ALL ? '' : categoryFilter,
    enabled: isAuthenticated,
    silentErrorToast: true,
  })

  const producersQuery = useMemberInsightProducersQuery({
    enabled: isAuthenticated,
  })

  const categoriesQuery = useMemberInsightCategoriesQuery({
    enabled: isAuthenticated,
  })

  useEffect((): void => {
    if (!listQuery.isError) {
      return
    }

    Toast.notify({
      type: toastTypes.ERROR,
      title: t('memberInsights.detail.loadErrorToastTitle'),
      message: t('memberInsights.detail.loadErrorToastMessage'),
      options: { toastId: 'member-insights-list-fetch-error' },
    })
  }, [listQuery.isError, t])

  const serverItems = useMemo((): IInsightEntity[] => {
    return listQuery.data?.items ?? []
  }, [listQuery.data?.items])

  const pageItems = useMemo(
    (): IInsightEntity[] =>
      applyProducerFilter({ items: serverItems, producer: producerFilter }),
    [serverItems, producerFilter],
  )

  const totalCount = listQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const producerOptions = useMemo<string[]>(
    () => producersQuery.data ?? [],
    [producersQuery.data],
  )

  const categoryOptions = useMemo<string[]>(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  )

  const isEmpty =
    listQuery.isSuccess && (listQuery.data?.items?.length ?? 0) === 0

  return {
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    isEmpty,
    searchTerm,
    categoryFilter,
    categoryOptions,
    isCategoriesLoading: categoriesQuery.isLoading,
    isCategoriesError: categoriesQuery.isError,
    producerFilter,
    producerOptions,
    currentPage,
    totalPages,
    pageItems,
    totalCount,
    selectedInsight,
    selectedInsightBlobUrl,
    setSearchTerm,
    setCategoryFilter,
    setProducerFilter,
    setCurrentPage,
    openInsightPreview,
    closeInsightPreview,
  }
}
